import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Apple, Beef, Wheat, Milk, Fish, Carrot, ShoppingCart,
  Plus, X, Trash2, AlertTriangle, Check, Users, Download,
  Utensils, ChevronRight, Settings, RefreshCw, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

// Complete food database with categories
const foodDatabase = {
  proteins: {
    name: 'Proteins',
    icon: Beef,
    color: 'bg-red-500',
    items: [
      { id: 'chicken_breast', name: 'Chicken Breast', unit: 'lbs', defaultQty: 2, tags: ['lean', 'poultry'] },
      { id: 'chicken_thighs', name: 'Chicken Thighs', unit: 'lbs', defaultQty: 2, tags: ['poultry'] },
      { id: 'ground_turkey', name: 'Ground Turkey', unit: 'lbs', defaultQty: 1, tags: ['lean', 'poultry'] },
      { id: 'ground_beef', name: 'Lean Ground Beef', unit: 'lbs', defaultQty: 1, tags: ['beef'] },
      { id: 'steak', name: 'Steak (Sirloin)', unit: 'lbs', defaultQty: 1, tags: ['beef'] },
      { id: 'salmon', name: 'Salmon Fillets', unit: 'lbs', defaultQty: 1, tags: ['fish', 'seafood'] },
      { id: 'tuna', name: 'Canned Tuna', unit: 'cans', defaultQty: 4, tags: ['fish', 'seafood'] },
      { id: 'shrimp', name: 'Shrimp', unit: 'lbs', defaultQty: 1, tags: ['shellfish', 'seafood'] },
      { id: 'eggs', name: 'Eggs', unit: 'dozen', defaultQty: 2, tags: ['eggs'] },
      { id: 'greek_yogurt', name: 'Greek Yogurt', unit: 'container', defaultQty: 2, tags: ['dairy'] },
      { id: 'cottage_cheese', name: 'Cottage Cheese', unit: 'container', defaultQty: 1, tags: ['dairy'] },
      { id: 'tofu', name: 'Tofu', unit: 'block', defaultQty: 1, tags: ['vegetarian', 'soy'] },
      { id: 'pork_chops', name: 'Pork Chops', unit: 'lbs', defaultQty: 1, tags: ['pork'] },
      { id: 'bacon', name: 'Turkey Bacon', unit: 'pack', defaultQty: 1, tags: ['poultry'] },
    ]
  },
  grains: {
    name: 'Grains & Carbs',
    icon: Wheat,
    color: 'bg-amber-500',
    items: [
      { id: 'brown_rice', name: 'Brown Rice', unit: 'lbs', defaultQty: 2, tags: ['gluten-free'] },
      { id: 'white_rice', name: 'White Rice', unit: 'lbs', defaultQty: 2, tags: ['gluten-free'] },
      { id: 'quinoa', name: 'Quinoa', unit: 'bag', defaultQty: 1, tags: ['gluten-free', 'protein'] },
      { id: 'oatmeal', name: 'Oatmeal', unit: 'container', defaultQty: 1, tags: ['oats'] },
      { id: 'whole_wheat_bread', name: 'Whole Wheat Bread', unit: 'loaf', defaultQty: 1, tags: ['wheat', 'gluten'] },
      { id: 'whole_wheat_pasta', name: 'Whole Wheat Pasta', unit: 'box', defaultQty: 2, tags: ['wheat', 'gluten'] },
      { id: 'sweet_potatoes', name: 'Sweet Potatoes', unit: 'lbs', defaultQty: 3, tags: ['gluten-free'] },
      { id: 'white_potatoes', name: 'White Potatoes', unit: 'lbs', defaultQty: 3, tags: ['gluten-free'] },
      { id: 'tortillas', name: 'Whole Wheat Tortillas', unit: 'pack', defaultQty: 1, tags: ['wheat', 'gluten'] },
      { id: 'rice_cakes', name: 'Rice Cakes', unit: 'pack', defaultQty: 1, tags: ['gluten-free'] },
    ]
  },
  produce: {
    name: 'Fruits & Vegetables',
    icon: Carrot,
    color: 'bg-green-500',
    items: [
      { id: 'bananas', name: 'Bananas', unit: 'bunch', defaultQty: 2, tags: ['fruit'] },
      { id: 'apples', name: 'Apples', unit: 'bag', defaultQty: 1, tags: ['fruit'] },
      { id: 'oranges', name: 'Oranges', unit: 'bag', defaultQty: 1, tags: ['fruit', 'citrus'] },
      { id: 'berries', name: 'Mixed Berries', unit: 'container', defaultQty: 2, tags: ['fruit'] },
      { id: 'grapes', name: 'Grapes', unit: 'bag', defaultQty: 1, tags: ['fruit'] },
      { id: 'broccoli', name: 'Broccoli', unit: 'heads', defaultQty: 2, tags: ['vegetable'] },
      { id: 'spinach', name: 'Spinach', unit: 'bag', defaultQty: 2, tags: ['vegetable', 'greens'] },
      { id: 'kale', name: 'Kale', unit: 'bunch', defaultQty: 1, tags: ['vegetable', 'greens'] },
      { id: 'bell_peppers', name: 'Bell Peppers', unit: 'each', defaultQty: 4, tags: ['vegetable'] },
      { id: 'carrots', name: 'Carrots', unit: 'bag', defaultQty: 1, tags: ['vegetable'] },
      { id: 'tomatoes', name: 'Tomatoes', unit: 'lbs', defaultQty: 2, tags: ['vegetable'] },
      { id: 'onions', name: 'Onions', unit: 'bag', defaultQty: 1, tags: ['vegetable'] },
      { id: 'avocados', name: 'Avocados', unit: 'each', defaultQty: 4, tags: ['fruit'] },
      { id: 'cucumber', name: 'Cucumbers', unit: 'each', defaultQty: 2, tags: ['vegetable'] },
      { id: 'lettuce', name: 'Romaine Lettuce', unit: 'head', defaultQty: 2, tags: ['vegetable', 'greens'] },
    ]
  },
  dairy: {
    name: 'Dairy & Alternatives',
    icon: Milk,
    color: 'bg-blue-400',
    items: [
      { id: 'milk', name: 'Whole Milk', unit: 'gallon', defaultQty: 1, tags: ['dairy'] },
      { id: 'skim_milk', name: 'Skim Milk', unit: 'gallon', defaultQty: 1, tags: ['dairy'] },
      { id: 'almond_milk', name: 'Almond Milk', unit: 'carton', defaultQty: 1, tags: ['dairy-free', 'nuts'] },
      { id: 'oat_milk', name: 'Oat Milk', unit: 'carton', defaultQty: 1, tags: ['dairy-free', 'oats'] },
      { id: 'cheese', name: 'Shredded Cheese', unit: 'bag', defaultQty: 1, tags: ['dairy'] },
      { id: 'string_cheese', name: 'String Cheese', unit: 'pack', defaultQty: 1, tags: ['dairy'] },
      { id: 'butter', name: 'Butter', unit: 'stick', defaultQty: 2, tags: ['dairy'] },
      { id: 'cream_cheese', name: 'Cream Cheese', unit: 'block', defaultQty: 1, tags: ['dairy'] },
    ]
  },
  pantry: {
    name: 'Pantry Staples',
    icon: Apple,
    color: 'bg-purple-500',
    items: [
      { id: 'peanut_butter', name: 'Peanut Butter', unit: 'jar', defaultQty: 1, tags: ['nuts', 'peanuts'] },
      { id: 'almond_butter', name: 'Almond Butter', unit: 'jar', defaultQty: 1, tags: ['nuts', 'tree-nuts'] },
      { id: 'honey', name: 'Honey', unit: 'bottle', defaultQty: 1, tags: [] },
      { id: 'olive_oil', name: 'Olive Oil', unit: 'bottle', defaultQty: 1, tags: [] },
      { id: 'almonds', name: 'Almonds', unit: 'bag', defaultQty: 1, tags: ['nuts', 'tree-nuts'] },
      { id: 'walnuts', name: 'Walnuts', unit: 'bag', defaultQty: 1, tags: ['nuts', 'tree-nuts'] },
      { id: 'protein_powder', name: 'Protein Powder', unit: 'container', defaultQty: 1, tags: ['dairy'] },
      { id: 'granola', name: 'Granola', unit: 'bag', defaultQty: 1, tags: ['oats'] },
      { id: 'dried_fruit', name: 'Dried Fruit Mix', unit: 'bag', defaultQty: 1, tags: ['fruit'] },
    ]
  }
};

// Common allergens
const commonAllergens = [
  { id: 'dairy', name: 'Dairy', icon: Milk },
  { id: 'eggs', name: 'Eggs', icon: Utensils },
  { id: 'peanuts', name: 'Peanuts', icon: Utensils },
  { id: 'tree-nuts', name: 'Tree Nuts', icon: Utensils },
  { id: 'fish', name: 'Fish', icon: Fish },
  { id: 'shellfish', name: 'Shellfish', icon: Fish },
  { id: 'wheat', name: 'Wheat/Gluten', icon: Wheat },
  { id: 'soy', name: 'Soy', icon: Utensils },
];

const FamilyMealPlanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [familySize, setFamilySize] = useState(4);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Family food preferences
  const [allergies, setAllergies] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [newDislike, setNewDislike] = useState('');
  
  // Grocery list
  const [selectedFoods, setSelectedFoods] = useState({});
  const [groceryList, setGroceryList] = useState([]);
  const [showGroceryList, setShowGroceryList] = useState(false);

  useEffect(() => {
    fetchFamilyData();
  }, []);

  const fetchFamilyData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch linked athletes
      const athletesRes = await axios.get(`${API}/family/athletes`, { headers });
      setAthletes(athletesRes.data.athletes || []);
      
      // Load saved preferences from localStorage (family-wide)
      const savedPrefs = localStorage.getItem('aura_family_food_prefs');
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        setAllergies(prefs.allergies || []);
        setDislikes(prefs.dislikes || []);
        setFamilySize(prefs.familySize || 4);
      }
      
      // Load selected foods
      const savedFoods = localStorage.getItem('aura_family_selected_foods');
      if (savedFoods) {
        setSelectedFoods(JSON.parse(savedFoods));
      } else {
        // Default selection: most common athlete foods
        const defaultSelection = {};
        Object.entries(foodDatabase).forEach(([catId, cat]) => {
          cat.items.forEach(item => {
            defaultSelection[item.id] = true;
          });
        });
        setSelectedFoods(defaultSelection);
      }
    } catch (error) {
      console.error('Failed to load family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = () => {
    setSaving(true);
    
    // Save to localStorage (for now)
    const prefs = {
      allergies,
      dislikes,
      familySize
    };
    localStorage.setItem('aura_family_food_prefs', JSON.stringify(prefs));
    localStorage.setItem('aura_family_selected_foods', JSON.stringify(selectedFoods));
    
    toast.success('Family preferences saved!');
    setSaving(false);
  };

  const toggleAllergen = (allergenId) => {
    setAllergies(prev => 
      prev.includes(allergenId) 
        ? prev.filter(a => a !== allergenId)
        : [...prev, allergenId]
    );
  };

  const addDislike = () => {
    if (!newDislike.trim()) return;
    if (!dislikes.includes(newDislike.toLowerCase())) {
      setDislikes([...dislikes, newDislike.toLowerCase()]);
    }
    setNewDislike('');
  };

  const removeDislike = (item) => {
    setDislikes(dislikes.filter(d => d !== item));
  };

  const toggleFood = (foodId) => {
    setSelectedFoods(prev => ({
      ...prev,
      [foodId]: !prev[foodId]
    }));
  };

  const isItemExcluded = (item) => {
    // Check if item matches any allergens or dislikes
    const itemTags = item.tags || [];
    const isAllergen = allergies.some(a => itemTags.includes(a));
    const isDisliked = dislikes.some(d => 
      item.name.toLowerCase().includes(d) || itemTags.includes(d)
    );
    return isAllergen || isDisliked;
  };

  const generateGroceryList = () => {
    const list = [];
    const scale = familySize / 4;
    
    // Check if any foods are selected
    const selectedCount = Object.values(selectedFoods).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.error('No foods selected! Please select items from the categories below first.');
      return;
    }
    
    Object.entries(foodDatabase).forEach(([catId, category]) => {
      category.items.forEach(item => {
        // Only include selected foods that aren't excluded
        if (selectedFoods[item.id] && !isItemExcluded(item)) {
          list.push({
            ...item,
            category: catId,
            categoryName: category.name,
            quantity: Math.ceil(item.defaultQty * scale),
            checked: false
          });
        }
      });
    });
    
    if (list.length === 0) {
      toast.warning('All selected items are excluded due to allergies or dislikes. Please adjust your selections.');
      return;
    }
    
    setGroceryList(list);
    setShowGroceryList(true);
    toast.success(
      <div>
        <p className="font-medium">Grocery List Ready!</p>
        <p className="text-sm opacity-80">{list.length} items for {familySize} people</p>
      </div>
    );
  };

  const toggleGroceryItem = (itemId) => {
    setGroceryList(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const copyListToClipboard = () => {
    const text = groceryList
      .filter(item => !item.checked)
      .map(item => `☐ ${item.quantity} ${item.unit} ${item.name}`)
      .join('\n');
    
    navigator.clipboard.writeText(text);
    toast.success('List copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-aura-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 text-aura-text-muted hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-green-500 rounded-sm flex items-center justify-center">
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">FAMILY MEAL PLAN</span>
          </div>
          <Button 
            onClick={savePreferences}
            disabled={saving}
            className="bg-aura-primary hover:bg-aura-primary-hover text-white"
            data-testid="save-prefs-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </header>

      <main className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Family Size */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-aura-secondary" />
                <div>
                  <p className="font-heading font-bold text-white uppercase tracking-tight">Family Size</p>
                  <p className="text-xs text-aura-text-muted">Adjust quantities based on household</p>
                </div>
              </div>
              <Select value={familySize.toString()} onValueChange={(v) => setFamilySize(parseInt(v))}>
                <SelectTrigger className="w-32 bg-aura-bg border-white/10 text-white" data-testid="family-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <SelectItem key={n} value={n.toString()} className="text-white">
                      {n} {n === 1 ? 'person' : 'people'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="allergies" className="w-full">
          <TabsList className="w-full bg-aura-surface border border-white/10 p-1 rounded-lg">
            <TabsTrigger value="allergies" className="flex-1 data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Allergies
            </TabsTrigger>
            <TabsTrigger value="dislikes" className="flex-1 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400">
              <X className="w-4 h-4 mr-2" />
              Dislikes
            </TabsTrigger>
            <TabsTrigger value="foods" className="flex-1 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
              <Apple className="w-4 h-4 mr-2" />
              Food List
            </TabsTrigger>
          </TabsList>

          {/* Allergies Tab */}
          <TabsContent value="allergies" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Food Allergies
                </CardTitle>
                <p className="text-sm text-aura-text-muted">
                  Select any food allergies in your family. These will be automatically excluded from grocery lists.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {commonAllergens.map(allergen => {
                    const isSelected = allergies.includes(allergen.id);
                    return (
                      <button
                        key={allergen.id}
                        onClick={() => toggleAllergen(allergen.id)}
                        className={`p-4 rounded-lg border transition-all text-center ${
                          isSelected 
                            ? 'bg-red-500/20 border-red-500 text-red-400' 
                            : 'bg-white/5 border-white/10 text-aura-text-muted hover:border-white/20'
                        }`}
                        data-testid={`allergen-${allergen.id}`}
                      >
                        <allergen.icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-red-400' : 'text-aura-text-muted'}`} />
                        <p className="text-sm font-medium">{allergen.name}</p>
                        {isSelected && (
                          <Badge className="mt-2 bg-red-500/30 text-red-300 border-0 text-xs">Excluded</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {allergies.length > 0 && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-300">
                      <AlertTriangle className="w-4 h-4 inline mr-2" />
                      Foods containing {allergies.map(a => commonAllergens.find(al => al.id === a)?.name).join(', ')} will be excluded from your grocery lists.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dislikes Tab */}
          <TabsContent value="dislikes" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <X className="w-5 h-5 text-yellow-400" />
                  Foods We Don't Eat
                </CardTitle>
                <p className="text-sm text-aura-text-muted">
                  Add specific foods your family doesn't like or want to avoid.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add dislike input */}
                <div className="flex gap-2">
                  <Input
                    value={newDislike}
                    onChange={(e) => setNewDislike(e.target.value)}
                    placeholder="e.g., mushrooms, brussels sprouts..."
                    className="bg-aura-bg border-white/10 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && addDislike()}
                    data-testid="dislike-input"
                  />
                  <Button onClick={addDislike} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="add-dislike-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Dislikes list */}
                {dislikes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {dislikes.map(item => (
                      <Badge 
                        key={item}
                        className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 pl-3 pr-1 py-1.5 text-sm capitalize"
                      >
                        {item}
                        <button 
                          onClick={() => removeDislike(item)}
                          className="ml-2 p-1 hover:bg-yellow-500/30 rounded-full"
                          data-testid={`remove-dislike-${item}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-aura-text-muted text-sm">No dislikes added yet. Add foods your family wants to avoid.</p>
                )}

                {/* Quick suggestions */}
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Quick Add</p>
                  <div className="flex flex-wrap gap-2">
                    {['mushrooms', 'olives', 'pickles', 'liver', 'brussels sprouts', 'anchovies', 'beets'].map(item => (
                      !dislikes.includes(item) && (
                        <button
                          key={item}
                          onClick={() => setDislikes([...dislikes, item])}
                          className="px-3 py-1 text-xs bg-white/5 text-aura-text-muted rounded-full hover:bg-white/10 capitalize"
                        >
                          + {item}
                        </button>
                      )
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Food List Tab */}
          <TabsContent value="foods" className="mt-4 space-y-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <Apple className="w-5 h-5 text-green-400" />
                  Select Foods for Your Family
                </CardTitle>
                <p className="text-sm text-aura-text-muted">
                  Check the foods your family eats. Unchecked items won't appear in grocery lists.
                </p>
              </CardHeader>
            </Card>

            {Object.entries(foodDatabase).map(([catId, category]) => {
              const Icon = category.icon;
              const availableItems = category.items.filter(item => !isItemExcluded(item));
              const excludedItems = category.items.filter(item => isItemExcluded(item));
              
              return (
                <Card key={catId} className="bg-aura-surface border-white/10">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 ${category.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="font-heading font-bold text-white uppercase tracking-tight">
                        {category.name}
                      </CardTitle>
                      <Badge className="bg-white/10 text-aura-text-muted border-0 ml-auto">
                        {availableItems.filter(i => selectedFoods[i.id]).length}/{availableItems.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {category.items.map(item => {
                        const isExcluded = isItemExcluded(item);
                        const isSelected = selectedFoods[item.id];
                        
                        return (
                          <button
                            key={item.id}
                            onClick={() => !isExcluded && toggleFood(item.id)}
                            disabled={isExcluded}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              isExcluded
                                ? 'bg-red-500/10 border-red-500/30 opacity-50 cursor-not-allowed'
                                : isSelected
                                  ? 'bg-green-500/10 border-green-500/50 text-white'
                                  : 'bg-white/5 border-white/10 text-aura-text-muted hover:border-white/20'
                            }`}
                            data-testid={`food-${item.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {isExcluded ? (
                                <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                              ) : isSelected ? (
                                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                              ) : (
                                <div className="w-4 h-4 border border-white/20 rounded flex-shrink-0" />
                              )}
                              <span className="text-sm truncate">{item.name}</span>
                            </div>
                            {isExcluded && (
                              <p className="text-xs text-red-400 mt-1 ml-6">Excluded</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>

        {/* Generate Grocery List Button */}
        <Card className="bg-gradient-to-r from-green-500/10 to-aura-primary/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-green-400" />
                  Generate Grocery List
                </h3>
                <p className="text-sm text-aura-text-muted mt-1">
                  Create a shopping list based on your selections for {familySize} people
                </p>
              </div>
              <Button 
                onClick={generateGroceryList}
                className="bg-green-500 hover:bg-green-600 text-white font-heading uppercase tracking-wider"
                data-testid="generate-list-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Generate List
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Grocery List Dialog */}
        <Dialog open={showGroceryList} onOpenChange={setShowGroceryList}>
          <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-green-400" />
                Grocery List ({groceryList.filter(i => !i.checked).length} items)
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Group by category */}
              {Object.entries(foodDatabase).map(([catId, category]) => {
                const catItems = groceryList.filter(item => item.category === catId);
                if (catItems.length === 0) return null;
                
                return (
                  <div key={catId}>
                    <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2 flex items-center gap-2">
                      <category.icon className="w-4 h-4" />
                      {category.name}
                    </p>
                    <div className="space-y-1">
                      {catItems.map(item => (
                        <button
                          key={item.id}
                          onClick={() => toggleGroceryItem(item.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                            item.checked 
                              ? 'bg-green-500/10 text-green-400 line-through' 
                              : 'bg-white/5 text-white hover:bg-white/10'
                          }`}
                        >
                          {item.checked ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <div className="w-4 h-4 border border-white/30 rounded" />
                          )}
                          <span className="flex-1 text-left text-sm">{item.name}</span>
                          <span className="text-xs text-aura-text-muted">
                            {item.quantity} {item.unit}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Completion Prompt */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-4">
              <p className="text-sm text-green-400 font-medium">What's Next?</p>
              <ul className="text-xs text-aura-text-muted mt-1 space-y-1">
                <li>• Tap items to check them off as you shop</li>
                <li>• Copy the list to share with family or use at the store</li>
                <li>• Save preferences to remember your selections</li>
              </ul>
            </div>
            
            <div className="flex gap-2 pt-4 border-t border-white/10">
              <Button 
                onClick={copyListToClipboard}
                className="flex-1 bg-aura-surface border border-white/10 text-white hover:bg-white/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Copy List
              </Button>
              <Button 
                onClick={() => {
                  setShowGroceryList(false);
                  toast.success('Grocery list ready! Don\'t forget to save your preferences.');
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default FamilyMealPlanPage;
